import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SurveyService from '~/api/services/survey'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { StyledGrid, StyledSegment, TableWrapper, CustomRow } from './style'
import { Button, Divider, Grid, Input, Popup, Table } from 'semantic-ui-react'
import dayjs from 'dayjs'
import { StyledGridColumns, StyledGridRow } from '../../MilkFixedPrice/ShowMilkFixedPrice/style'
import CustomIcon from '~/components/CustomIcon'
import { ReactSortable } from 'react-sortablejs'
import Dialog from '~/components/Dialog'
import TableComponent from '~/components/TableComponent'
import { LIGHT_USER_ROLE_SLUG, SURVEY_STATUS_KEYS } from '~/Constants'
import { useDebounce } from 'use-lodash-debounce';
import { useAuth } from '~/routes/components/AuthProvider'
import { SurveyQuery } from '~/QueryHooks/Survey'
import { handleErrors } from '~/utils/helpers'

const ShowSurvey: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: ''
  })
  const [search, setSearch] = useState('')
  const [showData, setShowData] = useState(null)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [questions, setQuestions] = useState([])
  const [deleteQuestionId, setDeleteQuestionId] = useState(null)
  const navigate = useNavigate()
  const debouncedSearchValue = useDebounce(search, 300)
  const tableColumnHeaders = [
    {
      name: t('Farmer group'),
      key: 'name'
    },
    {
      name: t('Completed / Total Farmers'),
      key: 'amountPercent',
      type: 'surveyParticipation'
    }
  ];

  const {
    isLoading: isBusyData,
    data: surveyTableData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = SurveyQuery.getSurveyFarmerTableData(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    }
  );

  useEffect(() => {
    fetchShowData()
  }, [t])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  async function fetchShowData() {
    setIsBusy(true);
    try {
      const responseShow = await SurveyService.getSurveyShowData(id)
      const responseQuestions = await SurveyService.getSurveyQuestions(id)
      setShowData(responseShow.data)
      setQuestions(responseQuestions.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderShow() {
    return <StyledSegment placeholder>
      <StyledGrid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <CustomRow>
              <Grid.Column>{t('Description')}</Grid.Column>
              <StyledGridColumns>{showData?.description || '-'}</StyledGridColumns>
            </CustomRow>
            <StyledGridRow>
              <Grid.Column>{t('Status')}</Grid.Column>
              <StyledGridColumns>{showData?.statusLabel}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <Grid.Column>{t('Start')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.start).format(('DD.MM.YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('End')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.end).format(('DD.MM.YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </StyledGrid>
    </StyledSegment>
  }

  function renderSortItems(disableSorting) {
    return questions.map((question, index) => {
      return <Table.Row style={{ cursor: disableSorting ? 'cursor' : 'grab' }} key={question.id}>
        <Table.Cell textAlign='center'>{`${index + 1}.`}</Table.Cell>
        <Table.Cell>{question?.question}</Table.Cell>
        <Table.Cell>{question?.typeLabel}</Table.Cell>
        <Table.Cell>
          <Button.Group>
            {showData?.status === SURVEY_STATUS_KEYS.defined && !lightUserLogged && <Popup
              content={t('Edit')}
              trigger={<Link to={RouteTypes.SURVEY_SHOW_QUESTION_EDIT.replace(':id', id).replace(':idQuestion', question.id)}>
                <CustomIcon name='pencil' size='large' />
              </Link>}
            />
            }
            <Popup
              content={t('Show')}
              trigger={<Link to={RouteTypes.SURVEY_SHOW_QUESTION_SHOW.replace(':id', id).replace(':idQuestion', question.id)}>
                <CustomIcon name='eye' size='large' />
              </Link>}
            />
            {showData?.status === SURVEY_STATUS_KEYS.defined && !lightUserLogged && <Popup
              content={t('Delete')}
              trigger={<CustomIcon
                name='trash'
                size='large'
                onClick={() => setDeleteQuestionId(question?.id)}
              />}
            />
            }
            <Popup
              content={t('Statistics')}
              trigger={<CustomIcon
                name='pie chart'
                size='large'
                onClick={() => navigate(RouteTypes.SURVEY_SHOW_QUESTION_STATISTICS.replace(':id', id).replace(':questionId', question?.id))}
              />}
            />
          </Button.Group>
        </Table.Cell>
      </Table.Row>
    })
  }

  async function handleSortQuestion() {
    try {
      await SurveyService.sortSurveyQuestions(id, { questions })
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderTable() {
    const disableSorting = showData?.status !== SURVEY_STATUS_KEYS.defined || lightUserLogged
    return <Table celled fixed singleLine>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={'1'}>#</Table.HeaderCell>
          <Table.HeaderCell width={'5'}>{t('Questions')}</Table.HeaderCell>
          <Table.HeaderCell width={'5'}>{t('Question type')}</Table.HeaderCell>
          <Table.HeaderCell width={'5'}>{t('Actions')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <ReactSortable
        disabled={disableSorting}
        list={questions}
        tag={'tbody'}
        animation={150}
        onSort={() => handleSortQuestion()}
        delayOnTouchOnly={true}
        delay={2}
        setList={setQuestions}
      >
        {renderSortItems(disableSorting)}
      </ReactSortable>
    </Table>
  }

  function renderActions() {
    return showData?.status === SURVEY_STATUS_KEYS.defined && <>
      <El.PageHeaderActionButton
        onClick={() => navigate(RouteTypes.SURVEY_SHOW_QUESTION_CREATE.replace(':id', id))}
      >{t('Create question')}
      </El.PageHeaderActionButton>
      <El.PageHeaderActionButton
        onClick={() => navigate(RouteTypes.SURVEY_SHOW_QUESTIONS_ADD.replace(':id', id))}
      >{t('Add questions')}
      </El.PageHeaderActionButton>
    </>
  }

  async function handleDeleteQuestion() {
    setIsBusy(true);
    try {
      await SurveyService.deleteQuestion(id, deleteQuestionId)
      notify(t('Successfully deleted question'), 'success')
      setQuestions(questions.filter(question => question.id !== deleteQuestionId))
      setDeleteQuestionId(null)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteModal() {
    return <Dialog
      isDialogOpen={Boolean(deleteQuestionId)}
      handleClose={() => setDeleteQuestionId(null)}
      textHeader={t('Delete question')}
      textContent={t('Do you really want to delete this question?')}
      textSubmit={t('Delete')}
      textClose={t('Close')}
      type={'abort'}
      handleSubmit={() => handleDeleteQuestion()}
    />
  }

  function renderFarmerGroupsTable() {
    return <TableWrapper>
      <div className='title'>
        <div>{t('Farmer groups')}</div>
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('Search...')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
      </div>
      <TableComponent
        tableColumnHeaders={tableColumnHeaders}
        animated
        isFetchingData={isFetchingData}
        dataFiltered={dataFiltered}
        handleChangeDataFiltered={(data) => setDataFiltered(data)}
        rows={rows}
        total={filtered}
      />
    </TableWrapper>
  }

  const { filtered, rows } = surveyTableData ? surveyTableData : { filtered: 0, rows: [] };

  return <Layout
    title={showData && `${showData?.title} (${showData?.uniqueId})`}
    isBusy={isBusy || isBusyData}
    segmented
    close={RouteTypes.SURVEY}
    whiteSpaceHeaderProps={'break-spaces'}
    actions={!lightUserLogged && renderActions()}
  >
    {deleteQuestionId && renderDeleteModal()}
    {renderShow()}
    {renderTable()}
    {renderFarmerGroupsTable()}
  </Layout>
}
export default ShowSurvey